// Directus-driven Updates feed for the Moments tab.
//
// Reads `${DIRECTUS_URL}/items/updates_public` — a PostgreSQL VIEW over the
// backing `updates` table that hardcodes `WHERE status='published' AND
// deleted_at IS NULL` in its definition. Server-side enforcement of the
// published-only rule lives IN that view (Directus Community edition doesn't
// support custom permission filters, so the view IS the filter). The raw
// `updates` collection is NOT exposed to the Public/Member policies — drafts
// and soft-deleted rows are unreachable via the API regardless of any filter
// a caller sends. The view also omits the `deleted_at` column, so it can't
// be surfaced through `fields=`.
//
// The frontend still does a client-side `status === 'published'` recheck as
// belt-and-braces, but the trust boundary is the view.
//
// Fetcher outcomes:
//   - Success (0..N rows) → { rows }
//   - Env gate OFF        → null  (feature disabled — caller shows honest empty)
//   - Directus down/403   → null  (same — caller shows honest empty, NOT stale
//                                  mock content. Mocks in prod look like real
//                                  MTFA promises we haven't delivered.)
//
// The collections behind this in Directus:
//   updates          (raw, admin-only): status/sort/published_at/title/summary/
//                    tag/tag_color/cover_url/link_type/link_id/external_url/
//                    service_contact/deleted_at
//   updates_public   (VIEW, public+member READ): everything except deleted_at,
//                    filtered to status='published' AND deleted_at IS NULL.

import { getDirectusUrl, tokenForRole } from './directus';

function parseJson(value, fallback) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

export function mapUpdateRow(row) {
  const extra = parseJson(row.extra, {}) || {};
  return {
    id: row.slug || row.id || extra.id,
    title: row.title || row.title_en || extra.title || '',
    body: row.summary || row.summary_en || row.body || extra.body || '',
    tag: row.tag || extra.tag || 'Update',
    tagColor: row.tag_color || extra.tagColor || 'bg-primary',
    image: row.cover_url || row.image_url || extra.image || '',
    time: formatTime(row.published_at || row.date_created || extra.published_at),
    publishedAt: row.published_at || row.date_created || null,
    linkType: row.link_type || extra.linkType || null,
    linkId: row.link_id || extra.linkId || null,
    externalUrl: row.external_url || extra.externalUrl || null,
    // service_contact is a KEY into mtfaContacts.MTFA_CONTACTS ('hq',
    // 'ihsan_casket', 'ikc_yishun', ...). Null means no WhatsApp handoff
    // shown for this card.
    serviceContact: row.service_contact || extra.serviceContact || null,
    // Client-side safety: even if server permissions ever miss, we filter.
    status: row.status || 'published',
  };
}

function formatTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

// Env gate. CRA/webpack replaces `process.env.REACT_APP_*` with a string
// literal at BUILD time — we must reference it directly (no `typeof process`
// guard, which stays as runtime code and evaluates false in the browser
// where `process` is undefined, silently defeating the whole switch).
const REMOTE_UPDATES_RAW = process.env.REACT_APP_REMOTE_UPDATES;
const REMOTE_UPDATES_ENABLED = REMOTE_UPDATES_RAW === 'true' || REMOTE_UPDATES_RAW === '1';

// Fetch the live updates feed. Returns:
//   { rows }         on success (may be [])
//   null             on any failure — caller shows honest empty state
export async function fetchDirectusUpdates({ role = 'member', signal } = {}) {
  if (!REMOTE_UPDATES_ENABLED) return null;
  const url = getDirectusUrl();
  if (!url) return null;
  const token = tokenForRole(role);
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  // The view hardcodes published + non-deleted server-side. `sort=-published_at,sort`
  // puts newest-first, with the sort column as a tiebreaker when timestamps match.
  // We also send the same `filter[status][_eq]=published` (redundant, but keeps
  // the wire request self-describing and lets a mis-provisioned raw-table
  // permission still degrade to safe behaviour).
  const qs = 'limit=-1&filter[status][_eq]=published&sort=-published_at,sort';
  try {
    const controller = signal ? undefined : new AbortController();
    const timer = controller ? setTimeout(() => controller.abort(), 15000) : null;
    const res = await fetch(`${url}/items/updates_public?${qs}`, {
      headers,
      signal: signal || controller?.signal,
    });
    if (timer) clearTimeout(timer);
    if (!res.ok) return null; // 403/404/500 → empty state
    const json = await res.json();
    const raw = Array.isArray(json?.data) ? json.data : [];
    const rows = raw
      .filter((r) => !r.deleted_at && (r.status ?? 'published') === 'published')
      .map(mapUpdateRow);
    return { rows };
  } catch {
    return null;
  }
}
